import { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  HMSNotificationTypes,
  selectAppData,
  useAutoplayError,
  useCustomEvent,
  useHMSActions,
  useHMSNotifications,
  useHMSStore,
} from "@100mslive/react-sdk";
import { toast } from "sonner";
import { SupportLink } from "@/components/common/SupportLink";
import { STOP_SCREENSHARE_EVENT } from "@/components/meeting/TileMenu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SIDE_PANES, useSidePane } from "@/hooks/useSidePane";
import { isInternalRole } from "@/lib/roles";

const PERMISSION_ERROR_CODES = [3001, 3011];
const AUTOPLAY_ERROR_CODE = 3008;

function blockedDevices(message = "") {
  const lower = message.toLowerCase();
  if (lower.includes("screen")) {
    return "Screen sharing";
  }
  const audio = lower.includes("audio");
  const video = lower.includes("video");
  if (audio && video) {
    return "Camera and microphone";
  }
  if (audio) {
    return "Microphone";
  }
  if (video) {
    return "Camera";
  }
  return null;
}

function initErrorCopy(error) {
  if (error.code === 4005 || /role/i.test(error.description || "")) {
    return {
      title: "This link has an unknown role",
      description:
        "The role at the end of the link doesn't exist for this interview. Use the full link from your interview email.",
    };
  }
  if (/disabled/i.test(error.description || "")) {
    return {
      title: "This interview room is closed",
      description: "The room for this interview can no longer be joined.",
    };
  }
  return {
    title: "We couldn't start the call",
    description:
      error.description ||
      error.message ||
      "Something went wrong while joining.",
  };
}

const usePathRoom = () => {
  const { pathname } = useLocation();
  const match = matchPath("/:section/:roomId/:role", pathname);
  return match?.params || {};
};

const Dialog = ({ open, title, children, actions, onOpenChange }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="flex flex-col gap-2">{children}</div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>{actions}</AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * Everything the SDK tells the participant: toasts for passing events
 * (messages, raised hands, reconnecting, being muted) and dialogs for what
 * needs an answer (unmute requests, blocked devices, autoplay, a lost
 * connection). Mounted once for the whole app.
 */
export const Notifications = () => {
  const navigate = useNavigate();
  const { section, roomId, role } = usePathRoom();
  const hmsActions = useHMSActions();
  const notification = useHMSNotifications();
  const isHeadless = useHMSStore(selectAppData("isHeadless"));
  const { sidePane, setSidePane } = useSidePane();
  const { error: autoplayError, unblockAudio, resetError } = useAutoplayError();
  const [unmuteRequest, setUnmuteRequest] = useState(null);
  const [permissionError, setPermissionError] = useState(null);
  const [initError, setInitError] = useState(null);
  const [disconnected, setDisconnected] = useState(null);
  const handsRaised = useRef(new Map());
  const reconnectToast = useRef(null);

  // These belong to the call: this component stays mounted for the whole
  // app, so leaving the call must not carry them onto the leave page.
  useEffect(() => {
    if (section !== "meeting") {
      setDisconnected(null);
      setUnmuteRequest(null);
    }
  }, [section]);

  useCustomEvent({
    type: STOP_SCREENSHARE_EVENT,
    onEvent: () => {
      hmsActions.setScreenShareEnabled(false).catch(() => {});
      toast("Your screen share was stopped by the interviewer");
    },
  });

  useEffect(() => {
    if (!notification) {
      return;
    }
    const { type, data, message } = notification;

    switch (type) {
      case HMSNotificationTypes.NEW_MESSAGE: {
        if (isHeadless || data?.ignored || sidePane === SIDE_PANES.chat) {
          break;
        }
        toast(`New message from ${data?.senderName || "someone"}`, {
          action: {
            label: "Open chat",
            onClick: () => setSidePane(SIDE_PANES.chat),
          },
        });
        break;
      }
      // Back after the rebuild dropped them (Alex, 2026-10-26): with two
      // people, knowing the other side arrived or dropped out is useful.
      case HMSNotificationTypes.PEER_JOINED:
      case HMSNotificationTypes.PEER_LEFT: {
        if (
          !data ||
          data.isLocal ||
          isHeadless ||
          isInternalRole(data.roleName)
        ) {
          break;
        }
        toast(
          `${data.name} ${type === HMSNotificationTypes.PEER_JOINED ? "joined" : "left"}`
        );
        break;
      }
      case HMSNotificationTypes.METADATA_UPDATED: {
        if (!data || data.isLocal || isHeadless) {
          break;
        }
        let isHandRaised = false;
        try {
          isHandRaised = Boolean(
            JSON.parse(data.metadata || "{}").isHandRaised
          );
        } catch {
          isHandRaised = false;
        }
        const wasRaised = handsRaised.current.get(data.id) || false;
        handsRaised.current.set(data.id, isHandRaised);
        if (isHandRaised && !wasRaised) {
          toast(`${data.name} raised hand`);
        }
        break;
      }
      case HMSNotificationTypes.RECONNECTING:
        reconnectToast.current = toast.warning(
          "You are offline for now. We're trying to reconnect you; please check your internet connection.",
          { duration: 10000 }
        );
        break;
      case HMSNotificationTypes.RECONNECTED:
        toast.success("You are now connected", { id: reconnectToast.current });
        break;
      case HMSNotificationTypes.CHANGE_TRACK_STATE_REQUEST: {
        if (isHeadless) {
          break;
        }
        if (data.enabled) {
          setUnmuteRequest({
            requestedBy: data.requestedBy?.name,
            tracks: [data.track],
            text: `your ${data.track.source} ${data.track.type}`,
          });
        } else {
          toast(
            `Your ${data.track.source} ${data.track.type} was muted by ${data.requestedBy?.name}.`
          );
        }
        break;
      }
      case HMSNotificationTypes.CHANGE_MULTI_TRACK_STATE_REQUEST: {
        if (isHeadless) {
          break;
        }
        if (data.enabled) {
          setUnmuteRequest({
            requestedBy: data.requestedBy?.name,
            tracks: data.tracks,
            text: "your tracks",
          });
        } else {
          toast(`You were muted by ${data.requestedBy?.name}.`);
        }
        break;
      }
      case HMSNotificationTypes.REMOVED_FROM_ROOM:
      case HMSNotificationTypes.ROOM_ENDED: {
        const reason = data?.reason ? ` Reason: ${data.reason}` : "";
        toast(`${message}.${reason}`);
        if (roomId && role) {
          setTimeout(() => navigate(`/leave/${roomId}/${role}`), 2000);
        }
        break;
      }
      case HMSNotificationTypes.DEVICE_CHANGE_UPDATE:
        if (message) {
          toast(message);
        }
        break;
      case HMSNotificationTypes.ERROR: {
        if (!data) {
          break;
        }
        if (data.code === AUTOPLAY_ERROR_CODE) {
          break;
        }
        if (PERMISSION_ERROR_CODES.includes(data.code)) {
          const devices = blockedDevices(data.message);
          if (devices) {
            setPermissionError({ devices, system: data.code === 3011 });
          }
          break;
        }
        if (data.action === "INIT") {
          setInitError(initErrorCopy(data));
          break;
        }
        if (data.isTerminal) {
          setDisconnected(
            data.message ||
              "We couldn't reconnect you. When you're back online, rejoin the interview."
          );
          break;
        }
        if (!isHeadless) {
          toast.error(data.message || "Something went wrong", {
            description: data.description,
          });
        }
        break;
      }
      default:
        break;
    }
    // Only a new notification should trigger this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  const rejoin = () => window.location.assign(`/preview/${roomId}/${role}`);

  return (
    <>
      <Dialog
        open={Boolean(autoplayError)}
        title="Allow audio"
        onOpenChange={open => !open && resetError()}
        actions={
          <AlertDialogAction
            onClick={async () => {
              await unblockAudio();
              resetError();
            }}
          >
            Allow audio
          </AlertDialogAction>
        }
      >
        Your browser blocked the call&apos;s sound until you allow it.
      </Dialog>

      <Dialog
        open={Boolean(unmuteRequest) && !isHeadless}
        title="Track Unmute Request"
        onOpenChange={open => !open && setUnmuteRequest(null)}
        actions={
          <>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                Promise.all(
                  unmuteRequest.tracks.map(track =>
                    hmsActions.setEnabledTrack(track.id, true)
                  )
                ).catch(() => {})
              }
            >
              Accept
            </AlertDialogAction>
          </>
        }
      >
        {unmuteRequest
          ? `${unmuteRequest.requestedBy} has requested you to unmute ${unmuteRequest.text}.`
          : null}
      </Dialog>

      <Dialog
        open={Boolean(permissionError)}
        title={
          permissionError ? `${permissionError.devices} access is blocked` : ""
        }
        onOpenChange={open => !open && setPermissionError(null)}
        actions={<AlertDialogAction>OK</AlertDialogAction>}
      >
        {permissionError
          ? `Allow ${permissionError.devices.toLowerCase()} access ${
              permissionError.system
                ? "in your system settings"
                : "from the address bar or your browser settings"
            }, then reload the page.`
          : null}
      </Dialog>

      <Dialog
        open={Boolean(initError)}
        title={initError?.title}
        actions={
          <AlertDialogAction onClick={rejoin}>Try again</AlertDialogAction>
        }
      >
        <span>{initError?.description}</span>
        <span>
          If you think this is a mistake on our side, please <SupportLink />.
        </span>
      </Dialog>

      <Dialog
        open={Boolean(disconnected)}
        title="You've been disconnected"
        actions={
          <>
            <AlertDialogCancel
              onClick={() => {
                setDisconnected(null);
                navigate(`/leave/${roomId}/${role}`);
              }}
            >
              Leave
            </AlertDialogCancel>
            <AlertDialogAction onClick={rejoin}>Rejoin</AlertDialogAction>
          </>
        }
      >
        {disconnected}
      </Dialog>
    </>
  );
};
